/**
 * Support SLA rules -- Aaim-operator (/support/sla).
 *
 * platform.aaim.com:9303 (Aaim-operator SPA). Lists and replaces the SLA
 * rule table that drives slaTargetAt on newly-created cases and the
 * first-response window the breach scheduler (node 5, C2) watches.
 *
 *   GET /api/v1/support/sla
 *   PUT /api/v1/support/sla   (replaces the entire rule set)
 *
 * The spec's PUT body is `array<SupportSlaRule>` -- there is no partial
 * upsert path, replacement is wholesale. The page lets the operator edit
 * the rules in place and then submit the complete set as one PUT.
 *
 * Because the PUT replaces every rule, editing opens only once the GET has
 * returned the stored set (GH #1941, operator ruling 2026-10-10). While it
 * loads, or after it fails, the page shows no rule list, and Add and Save
 * stay disabled: a Save then would replace the stored rules with an empty or
 * partial set. Retry reloads.
 *
 * Ported from the orphaned admin-portal Support/SupportSla.tsx (node-5
 * plan item B3); the load gating followed it on GH #678 (operator ruling
 * 2026-10-23), which moved the Support operator tooling out of admin-portal.
 * Audience platform-admin ONLY. No top-level view-model interfaces.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BodyText,
  Button,
  Card,
  Input,
  Select,
  Alert,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/support-platform';
import { useAuthStore } from '../../stores/auth';
import { supportPlatformClient } from '../../api/typed-clients';
import { SupportServiceMissingError, SupportServiceNotice, supportFailure } from './SupportServiceNotice';

type SupportSlaRule = components['schemas']['SupportSlaRule'];
type SupportCasePriority = components['schemas']['SupportCasePriority'];

const PLATFORM_ADMIN_ROLES = [
  'aaim_super_admin',
  'platform_admin',
  'e2e_test_admin',
] as const;

const CATEGORY_OPTIONS = [
  'account',
  'quiltt_connection',
  'pledge',
  'kyc',
  'billing',
  'security',
  'feedback',
  'bug_report',
  'other',
] as const;

const PRIORITY_OPTIONS: SupportCasePriority[] = ['P1', 'P2', 'P3', 'P4'];

function emptyRule(): SupportSlaRule {
  return {
    category: 'account',
    priority: 'P3',
    firstResponseMinutes: 60,
    resolutionMinutes: 1440,
  };
}

export function SupportSlaPage() {
  const user = useAuthStore((s) => s.user);
  const roles = useMemo(
    () => (user?.roles ?? []).map((r) => (typeof r === 'string' ? r.toLowerCase() : r)),
    [user],
  );
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.some((r) => roles.includes(r));

  const [rules, setRules] = useState<SupportSlaRule[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serviceMissing, setServiceMissing] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // The rule list on screen is the stored set only after a successful load
  // (GH #1941): PUT replaces every stored rule, so Add and Save wait for it.
  const rulesLoaded = !loading && loadError === null && !serviceMissing;

  const loadRules = useCallback(async () => {
    if (!isPlatformAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setServiceMissing(false);
    try {
      const { data, error, response } = await supportPlatformClient.GET('/api/v1/support/sla');
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to load SLA rules');
      setRules(data?.rules ?? []);
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setLoadError(err instanceof Error ? err.message : 'Failed to load SLA rules');
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    void loadRules();
  }, [loadRules]);

  const handleAddRule = useCallback(() => {
    setRules((prev) => [...prev, emptyRule()]);
    setSaveMessage(null);
  }, []);

  const handleRemoveRule = useCallback((idx: number) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
    setSaveMessage(null);
  }, []);

  const handleRuleField = useCallback(
    <K extends keyof SupportSlaRule>(idx: number, key: K, value: SupportSlaRule[K]) => {
      setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
      setSaveMessage(null);
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!isPlatformAdmin || !rulesLoaded) return;
    // Coerce minutes fields back to integers; the inputs are number-typed but
    // produce string values, which the PUT body schema rejects.
    const normalized = rules.map<SupportSlaRule>((r) => ({
      category: r.category,
      priority: r.priority,
      firstResponseMinutes: Math.max(1, Number(r.firstResponseMinutes) || 1),
      resolutionMinutes: Math.max(1, Number(r.resolutionMinutes) || 1),
    }));
    setSaving(true);
    setSaveError(null);
    setSaveMessage(null);
    try {
      const { error, response } = await supportPlatformClient.PUT('/api/v1/support/sla', {
        body: normalized,
      });
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to save SLA rules');
      setRules(normalized);
      setSaveMessage('SLA rules saved.');
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setSaveError(err instanceof Error ? err.message : 'Failed to save SLA rules');
    } finally {
      setSaving(false);
    }
  }, [isPlatformAdmin, rulesLoaded, rules]);

  if (!isPlatformAdmin) {
    return (
      <div className="p-6 space-y-4">
        <Card>
          <div className="p-4 space-y-3">
            <Alert type="error">
              SLA rules are visible to platform-admin staff only.
            </Alert>
            <Link to="/support" className="text-primary hover:underline text-sm">
              &larr; Back to cases
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const categoryOptions = CATEGORY_OPTIONS.map((c) => ({ value: c, label: c }));
  const priorityOptions = PRIORITY_OPTIONS.map((p) => ({ value: p, label: p }));

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <BodyText size="sm" color="secondary">
            Service-level agreement rules. First-response and resolution windows
            in minutes per (category, priority) pair. Replacing the rule set
            applies wholesale on save. The breach scheduler escalates cases past
            either window.
          </BodyText>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={handleAddRule} disabled={!rulesLoaded}>
            Add rule
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !rulesLoaded}>
            {saving ? 'Saving...' : 'Save rules'}
          </Button>
        </div>
      </div>

      {serviceMissing && <SupportServiceNotice />}
      {loadError && <Alert type="error">{loadError}</Alert>}
      {saveError && <Alert type="error">{saveError}</Alert>}
      {saveMessage && <Alert type="success">{saveMessage}</Alert>}

      <Card>
        <div className="p-4 space-y-3">
          {loading ? (
            <BodyText size="sm" color="secondary">
              Loading...
            </BodyText>
          ) : loadError !== null || serviceMissing ? (
            <div className="flex flex-wrap items-center gap-3">
              <BodyText size="sm" color="secondary">
                The rules can be edited once the current set loads.
              </BodyText>
              <Button variant="secondary" onClick={() => void loadRules()}>
                Retry
              </Button>
            </div>
          ) : rules.length === 0 ? (
            <BodyText size="sm" color="secondary">
              No SLA rules configured. Click "Add rule" to add one.
            </BodyText>
          ) : (
            <ul className="space-y-3">
              {rules.map((r, idx) => (
                <li
                  key={`${r.category}-${r.priority}-${idx}`}
                  className="grid grid-cols-1 md:grid-cols-5 gap-3 border-b border-recessed pb-3 last:border-0"
                >
                  <Select
                    label="Category"
                    options={categoryOptions}
                    value={r.category}
                    onChange={(e) => handleRuleField(idx, 'category', e.target.value)}
                  />
                  <Select
                    label="Priority"
                    options={priorityOptions}
                    value={r.priority}
                    onChange={(e) =>
                      handleRuleField(idx, 'priority', e.target.value as SupportCasePriority)
                    }
                  />
                  <Input
                    label="First response (min)"
                    type="number"
                    value={String(r.firstResponseMinutes)}
                    onChange={(e) =>
                      handleRuleField(
                        idx,
                        'firstResponseMinutes',
                        Math.max(1, Number(e.target.value) || 1),
                      )
                    }
                  />
                  <Input
                    label="Resolution (min)"
                    type="number"
                    value={String(r.resolutionMinutes)}
                    onChange={(e) =>
                      handleRuleField(
                        idx,
                        'resolutionMinutes',
                        Math.max(1, Number(e.target.value) || 1),
                      )
                    }
                  />
                  <div className="flex items-end">
                    <Button variant="ghost" onClick={() => handleRemoveRule(idx)}>
                      Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}
